import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CONSULTOR, NAV_LINKS, WA_DEFAULT } from "../../data/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    window.__lenis?.scrollTo(href, { offset: -72, duration: 1.4 });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter,box-shadow] duration-500 ${
        scrolled
          ? "border-b border-white/5 bg-cream/85 shadow-[0_8px_30px_-18px_rgba(0,0,0,0.6)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#top" onClick={go("#top")} data-testid="navbar-logo" className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-champagne/40 bg-surface font-display text-sm font-semibold text-ink">
            LR
          </span>
          <span className="leading-tight">
            <span className="block font-display text-base font-semibold tracking-tight text-ink">
              {CONSULTOR.name}
            </span>
            <span className="block font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-champagne-dark">
              Consultor de Planos de Saúde
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-[13px] font-medium tracking-wide text-stone-400 transition-colors duration-300 hover:text-ink"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-cta"
            className="group hidden items-center gap-2 rounded-full bg-wa px-5 py-2.5 text-[13px] font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-wa-dark sm:inline-flex"
          >
            <WhatsAppIcon className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
            Fale comigo agora
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="navbar-menu-toggle"
            aria-label="Abrir menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/5 bg-cream lg:hidden"
            data-testid="navbar-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={go(l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-stone-300 hover:bg-white/5"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="navbar-mobile-whatsapp-cta"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-wa px-5 py-3 text-sm font-bold text-white"
              >
                <WhatsAppIcon className="h-4 w-4" />
                Fale comigo agora pelo WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
